import { auth } from '../services/firebase';
import { logError, LogErrorOptions, LogSeverity } from '../services/logger';

/**
 * Centralized error logging used by the global error handlers, the React error
 * boundary, and any callsite that wants explicit context/metadata attached
 * before the entry hits Cloud Logging. Mirrors aligned's GlobalErrorLogger
 * surface. Adds two things over the basic `logError`:
 *   1. Auto-attaches the current Firebase user (uid + email).
 *   2. Debounces identical messages within a 2s window so a render loop can't
 *      flood Error Reporting.
 */

const DEBOUNCE_MS = 2000;
const recentErrorAt = new Map<string, number>();

function shouldDebounce(messageKey: string): boolean {
  const now = Date.now();
  const last = recentErrorAt.get(messageKey);
  if (last !== undefined && now - last < DEBOUNCE_MS) {
    return true;
  }
  recentErrorAt.set(messageKey, now);
  if (recentErrorAt.size > 200) {
    const cutoff = now - DEBOUNCE_MS;
    recentErrorAt.forEach((at, key) => {
      if (at < cutoff) recentErrorAt.delete(key);
    });
  }
  return false;
}

function currentAuthUser(): { uid?: string; email?: string | null } | undefined {
  try {
    const u = auth.currentUser;
    if (!u) return undefined;
    return { uid: u.uid, email: u.email };
  } catch {
    return undefined;
  }
}

function debounceKey(error: unknown, contextLabel?: string): string {
  const msg =
    error instanceof Error
      ? `${error.name}:${error.message}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return String(error);
            } catch {
              return 'unknown';
            }
          })();
  return contextLabel ? `${contextLabel}::${msg}` : msg;
}

export interface GlobalLogOptions {
  /** Short label naming the originating site (e.g. "TaskQueue.subscribe"). */
  context?: string;
  /** Arbitrary structured fields attached to the log payload. */
  metadata?: Record<string, unknown>;
  /** Force a severity. */
  severity?: LogSeverity;
}

export function logGlobalError(error: unknown, options: GlobalLogOptions = {}): void {
  const key = debounceKey(error, options.context);
  if (shouldDebounce(key)) return;

  const payload: LogErrorOptions = {
    context: options.context,
    metadata: options.metadata,
    user: currentAuthUser(),
  };
  if (options.severity) payload.severity = options.severity;
  logError(error, payload);
}

/** Log an error from a Firestore / async stream onError handler. */
export function logStreamError(error: unknown, options: GlobalLogOptions = {}): void {
  logGlobalError(error, options);
}

export const _internal = { shouldDebounce, debounceKey, recentErrorAt };
